/**
 * One place that understands the backend's error envelope.
 *
 * Every non-2xx response from the API carries the same shape (services/api/src/shared/errors.ts):
 *
 *   { error: { code, message, requestId?, details? } }
 *
 * Before this module each feature dug into `error.response?.data.error.code` on its own, which
 * meant three things were routinely lost: the `message` the backend wrote for the user, the
 * per-field `details.issues` a rejected form needs, and the `requestId` that makes a failure
 * findable in Admin → Errors. {@link describeApiError} normalises all of it — including the
 * cases that are NOT an envelope at all (a network failure, a cancelled request, an HTML error
 * page from a proxy), which must not be reported as if the backend had explained itself.
 *
 * Presentation policy (DEC-024, R-18.65): the backend's `message` is English and un-i18n'd, so
 * it is NEVER the user-facing headline. Callers show a translated headline and attach
 * {@link technicalSummary} — code + requestId — as the secondary line an operator can act on.
 * The full backend text stays available for the diagnostics log and the copy-to-clipboard path.
 */

import { isAxiosError } from 'axios';

/** One per-field validation issue, as `validateBody`/the OpenAPI validator report them. */
export interface ApiIssue {
  path: string;
  message: string;
}

/** The backend envelope, normalised — every field optional because any of them may be absent. */
export interface ApiErrorInfo {
  /** HTTP status, when the request got a response at all. */
  status?: number;
  /** Machine-readable code (VALIDATION_ERROR, ACTION_BLOCKED…), when the body was an envelope. */
  code?: string;
  /** The backend's human-readable message. English — never render it as the primary headline. */
  message?: string;
  /** Correlation id — the operator's lookup key in Admin → Errors. */
  requestId?: string;
  /** Aggregated per-field validation issues, when the failure was a validation rejection. */
  issues: ApiIssue[];
  /** The whole `details` object, for the diagnostics view. */
  details?: Record<string, unknown>;
  /** True when the request never produced an HTTP response (offline, DNS, CORS, timeout). */
  isNetworkError: boolean;
  /** True when the failure is an authorization denial (401/403). */
  isAuthError: boolean;
}

/** The envelope shape as it arrives on the wire, before any narrowing. */
interface RawErrorBody {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
    details?: unknown;
  };
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Narrow `details.issues` to the per-field array, tolerating a partially-shaped payload. */
function readIssues(details: Record<string, unknown> | undefined): ApiIssue[] {
  const raw = details?.issues;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((issue) => {
    if (typeof issue !== 'object' || issue === null) return [];
    const path = asString((issue as { path?: unknown }).path);
    const message = asString((issue as { message?: unknown }).message);
    return message === undefined ? [] : [{ path: path ?? '', message }];
  });
}

const AUTH_STATUSES = new Set([401, 403]);

/**
 * Normalise ANY thrown value into {@link ApiErrorInfo}. Never throws and never assumes the
 * body is an envelope: a proxy timeout returns HTML, an aborted request has no response at
 * all, and a bug in the app itself throws a plain `Error`. Each of those yields a description
 * with `code`/`message` absent rather than a fabricated one.
 */
export function describeApiError(error: unknown): ApiErrorInfo {
  if (!isAxiosError(error)) {
    return { issues: [], isNetworkError: false, isAuthError: false };
  }

  const status = error.response?.status;
  // No response object at all: the request never reached the API (offline, DNS, CORS,
  // timeout, or an aborted navigation). Reporting a backend code here would be a lie.
  if (error.response === undefined) {
    return { issues: [], isNetworkError: true, isAuthError: false };
  }

  const body = error.response.data as RawErrorBody | undefined;
  const envelope = typeof body === 'object' && body !== null ? body.error : undefined;
  const details =
    typeof envelope?.details === 'object' && envelope.details !== null
      ? (envelope.details as Record<string, unknown>)
      : undefined;

  return {
    ...(status !== undefined && { status }),
    ...(asString(envelope?.code) !== undefined && { code: asString(envelope?.code) }),
    ...(asString(envelope?.message) !== undefined && { message: asString(envelope?.message) }),
    ...(asString(envelope?.requestId) !== undefined && {
      requestId: asString(envelope?.requestId),
    }),
    ...(details !== undefined && { details }),
    issues: readIssues(details),
    isNetworkError: false,
    isAuthError: status !== undefined && AUTH_STATUSES.has(status),
  };
}

/**
 * The compact technical line shown UNDER a translated headline: `CODE · id 3f2a91c4`.
 * Returns an empty string when the failure carried neither — there is nothing to quote, and a
 * dangling separator reads like a rendering bug.
 *
 * The requestId is truncated to its first segment: that is enough to find the row in Admin →
 * Errors (the filter is a prefix-free exact match on a value with 4 billion first-segment
 * variants) and short enough to read aloud over a phone.
 */
export function technicalSummary(info: ApiErrorInfo): string {
  const parts: string[] = [];
  if (info.code !== undefined) parts.push(info.code);
  if (info.requestId !== undefined) parts.push(`id ${info.requestId.split('-')[0]}`);
  return parts.join(' · ');
}

/**
 * True when the error is an HTTP 403 — the authorization denial screens treat as a DISTINCT,
 * non-retryable state: retrying cannot grant a missing capability, so the UI never offers it.
 */
export function isForbidden(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}
